import { DateTime } from 'luxon'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useRef } from 'react'
import {
  SecondaryTextContainer,
  TabListTitle
} from '../../../../components/stories/organisms/top-navigation/components/global-search/styled-components'
import {
  TaskStatusClosed,
  TaskStatusDone,
  TaskStatusInactive,
  TaskStatusOpen,
  TaskStatusOpenOverdue,
  TaskStatusReject
} from '../../../../components/stories/atoms/icons/task-preview'
import GlobalSearchNotFound from '../../../../components/stories/organisms/top-navigation/components/global-search/components/not-found'
import { GlobalSearchTabContainer } from '../../../../components/stories/organisms/top-navigation/styled-components'
import HighlightTitle from '../../../../components/stories/organisms/top-navigation/components/global-search/components/highligh-title'
import StatusIndicationComponent from '../../../../components/stories/atoms/status-indication'
import {
  statusIndicationMemberOptions,
  statusIndicationMemberVariantOptions
} from '../../../../components/stories/atoms/status-indication/constants'
import ContextMenuComponent from '../../../../components/stories/organisms/context-menu'
import { taskPreviewTaskStatusOptions } from '../../../../components/stories/molecules/task-preview/constants'
import TopBarLoadingContent from '../../../../components/stories/organisms/top-navigation/components/loading-content'
import { useOnInView } from 'react-intersection-observer'
import QuickSelectComponent from '../../../../components/stories/organisms/top-navigation/components/quick-select'
import { usersAPI } from '../../../../../proxyConfig/apiUrls'
import { useAccessSearchParam } from '../../../../zustand/authentication/use-auth-store'
import { FormattedMessage } from 'react-intl'

const CustomTasksTab = ({ search, tasksQuery = null, handleNavigateTask }) => {
  const accessSearchParam = useAccessSearchParam()

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    tasksQuery

  const allTasks = data?.pages.flatMap((d) => d.content) || []

  const onBottomReached = useCallback(() => {
    hasNextPage && !isFetchingNextPage && fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const onBottomReachedRef = useRef(onBottomReached)

  useEffect(() => {
    onBottomReachedRef.current = onBottomReached
  }, [onBottomReached])

  const bottomReachedRef = useOnInView(
    useCallback((inView) => {
      if (inView) {
        onBottomReachedRef.current?.()
      }
    }, []),
    {
      rootMargin: '20px',
      skip: !hasNextPage
    }
  )

  const SecondaryText = ({ taskNumber, lastModified, pinName, pinNumber }) => {
    const relativeTime = DateTime.fromISO(lastModified).toRelative()

    return (
      <SecondaryTextContainer>
        #{taskNumber} • {relativeTime} • {pinName} ({pinNumber})
      </SecondaryTextContainer>
    )
  }

  const Leadingicon = ({ taskState }) => {
    const iconMap = {
      [taskPreviewTaskStatusOptions.OPEN]: <TaskStatusOpen />,
      [taskPreviewTaskStatusOptions.DONE]: <TaskStatusDone />,
      [taskPreviewTaskStatusOptions.CLOSED]: <TaskStatusClosed />,
      [taskPreviewTaskStatusOptions.OVERDUE]: <TaskStatusOpenOverdue />,
      [taskPreviewTaskStatusOptions.INACTIVE]: <TaskStatusInactive />,
      [taskPreviewTaskStatusOptions.REJECTED]: <TaskStatusReject />
    }

    return iconMap[taskState] || null
  }

  if (isLoading) {
    return <TopBarLoadingContent />
  }

  if (isEmpty(allTasks)) {
    return <GlobalSearchNotFound />
  }

  const avatarVariantOption = (type) => {
    if (type === 'TeamGroup') {
      return statusIndicationMemberVariantOptions.TEAM
    }
    if (type === 'Contact') {
      return statusIndicationMemberVariantOptions.CONTACT
    }
    return statusIndicationMemberVariantOptions.MEMBER
  }

  return (
    <GlobalSearchTabContainer>
      {!search && (
        <TabListTitle>
          <FormattedMessage
            id="recently_updated"
            style={{
              textTransform: 'uppercase'
            }}
          />
        </TabListTitle>
      )}
      {allTasks.map((task, index) => (
        <ContextMenuComponent.Item
          key={task.id}
          id={task.id}
          title={<HighlightTitle title={task.title} query={search} />}
          secondaryText={
            <SecondaryText
              taskNumber={task.number}
              lastModified={task.lastModified}
              pinName={task.issue.title}
              pinNumber={task.issue.number}
            />
          }
          leadingIcon={<Leadingicon taskState={task.currentState} />}
          trailingIcon={
            <StatusIndicationComponent
              image={`${usersAPI}/${task.assignment.id}/avatar${accessSearchParam()}`}
              status={statusIndicationMemberOptions[task.assignment.state]}
              variant={avatarVariantOption(task.assignment.type)}
            />
          }
          style={{ width: '100%', padding: '0.25rem 0.5rem' }}
          onClick={() => handleNavigateTask(task)}
        />
      ))}
      {isFetchingNextPage && <QuickSelectComponent.LoadingItem />}

      <div
        ref={bottomReachedRef}
        style={{
          height: '1px'
        }}
      />
    </GlobalSearchTabContainer>
  )
}

CustomTasksTab.propTypes = {
  onClickAction: PropTypes.func,
  search: PropTypes.string,
  projectId: PropTypes.string
}

export default CustomTasksTab
